
/**
 * AfterShip checkout watchdog for the Shopify cart drawer.
 *
 * Configure window.aftershipWatchdogConfig BEFORE loading this file:
 * {
 *   timeoutMs: 5000,
 *   recoveryTimeoutMs: 3000,
 *   protectionVariantId: null,
 *   protectionProperties: {},
 *   debug: false
 * }
 *
 * A protection variant must be verified against AfterShip's actual cart payload.
 * This script does not guarantee coverage eligibility for manually added items.
*/
class AftershipWatchdog {
    constructor( config ) {
        this.config = Object.assign({
            timeoutMs: 5000,
            recoveryTimeoutMs: 3000,
            protectionVariantId: null,
            protectionProperties: {},
            debug: false
        }, config);

        this.widget = null;
        this.root = null;
        this.primaryButton = null;
        this.secondaryButton = null;
        this.attempt = null;
        this.timer = null;
        this.observer = null;
        this.recoveryTimer = null;
        this.checkoutStarted = false;
        this.widgetObserver = null;
        this.initializationTimer = null;
        this.navigationTimer = null;
        this.recoveryController = null;
        this.destroyed = false;
        this.attemptId = 0;

        this.onPrimaryClickBound = this.onPrimaryClick.bind( this );
        this.onSecondaryClickBound = this.onSecondaryClick.bind( this );
        this.onPageHideBound = this.onPageHide.bind( this );
        this.onMutationsBound = this.onMutations.bind( this );
        this.onDocumentReadyBound = this.onDocumentReady.bind( this );
        this.onPrimaryKeydownBound = this.onPrimaryKeydown.bind( this );
        this.onSecondaryKeydownBound = this.onSecondaryKeydown.bind( this );
        this.onTimeoutBound = this.onTimeout.bind( this );
        this.onRecoveryTimeoutBound = this.onRecoveryTimeout.bind( this );
        this.onNavigationTimeoutBound = this.onNavigationTimeout.bind( this );
        this.onInitializationCheckBound = this.onInitializationCheck.bind( this );

        this.initialize();
    }

    log( message, details ) {
        if (this.config.debug === true) {
            console.info( '[AfterShip Watchdog]', message, details );
        }
    }

    initialize() {
        if ( document.readyState === 'loading' ) {
            document.addEventListener( 'DOMContentLoaded', this.onDocumentReadyBound, { once: true } );
        } else {
            this.onDocumentReady();
        }
    }

   onDocumentReady() {
        if ( this.destroyed === false && document.body !== null ) {
            this.connect();
            this.observer = new MutationObserver( this.onMutationsBound );
            this.observer.observe( document.body, { childList: true, subtree: true } );
            this.initializationTimer = window.setInterval( this.onInitializationCheckBound, 500 );
            window.addEventListener( 'pagehide', this.onPageHideBound );
        }
    }

    onInitializationCheck() {
        if ( this.destroyed === false ) {
            this.connect();
        }
    }

    onMutations() {
        if ( this.destroyed === false ) {
            this.connect();
        }
    }

    connect() {
        const widget = document.querySelector( 'cart-drawer aftership-protection' );
        const root = widget !== null && widget.appRoot !== undefined && widget.appRoot !== null ? widget.appRoot : null;
        let primaryButton = null;
        let secondaryButton = null;

        if ( root !== null && typeof root.querySelector === 'function' ) {
            primaryButton = root.querySelector( '[data-checkout-button-slot="primary"]' );
            secondaryButton = root.querySelector( 'button[class*="checkoutSecondaryButton"]' );
        }

        if ( this.widget !== widget || this.root !== root ) {
            this.disconnectButtons();
            this.disconnectWidgetObserver();
            this.widget = widget;
            this.root = root;
            this.primaryButton = null;
            this.secondaryButton = null;

            if ( root !== null ) {
                this.widgetObserver = new MutationObserver( this.onMutationsBound );
                this.widgetObserver.observe( root, { childList: true, subtree: true } );
            }
        }

        if ( this.primaryButton !== primaryButton || this.secondaryButton !== secondaryButton ) {
            this.disconnectButtons();
            this.primaryButton = primaryButton;
            this.secondaryButton = secondaryButton;

            if ( this.primaryButton !== null ) {
                this.primaryButton.addEventListener( 'click', this.onPrimaryClickBound, true );
                this.primaryButton.addEventListener( 'keydown', this.onPrimaryKeydownBound, true );
            }

            if ( this.secondaryButton !== null ) {
                this.secondaryButton.addEventListener( 'click', this.onSecondaryClickBound, true );
                this.secondaryButton.addEventListener( 'keydown', this.onSecondaryKeydownBound, true );
            }

            this.log( 'Widget buttons connected', { primary: primaryButton !== null, secondary: secondaryButton !== null } );
        }
    }

    disconnectButtons() {
        if ( this.primaryButton !== null ) {
            this.primaryButton.removeEventListener( 'click', this.onPrimaryClickBound, true );
            this.primaryButton.removeEventListener( 'keydown', this.onPrimaryKeydownBound, true );
        }

        if ( this.secondaryButton !== null ) {
            this.secondaryButton.removeEventListener( 'click', this.onSecondaryClickBound, true );
            this.secondaryButton.removeEventListener( 'keydown', this.onSecondaryKeydownBound, true );
        }
    }

    disconnectWidgetObserver() {
        if ( this.widgetObserver !== null ) {
            this.widgetObserver.disconnect();
            this.widgetObserver = null;
        }
    }

    onPrimaryClick() {
        this.startAttempt( true );
    }

    onSecondaryClick() {
        this.startAttempt( false );
    }

    onPrimaryKeydown( event ) {
        if ( event.key === 'Enter' || event.key === ' ' ) {
            this.startAttempt( true );
        }
    }

    onSecondaryKeydown( event ) {
        if ( event.key === 'Enter' || event.key === ' ' ) {
            this.startAttempt( false );
        }
    }

    startAttempt( wantsProtection ) {
        if ( this.destroyed === false && this.attempt === null && this.checkoutStarted === false ) {
            this.attemptId++;
            this.attempt = {
                id: this.attemptId,
                wantsProtection: wantsProtection,
                recovering: false
            };

            this.timer = window.setTimeout( this.onTimeoutBound, this.config.timeoutMs );
            this.log( 'Checkout attempt started', { wantsProtection: wantsProtection } );
        }
    }

    onTimeout() {
        this.timer = null;

        if ( this.attempt !== null && this.attempt.recovering === false && this.checkoutStarted === false ) {
            this.attempt.recovering = true;
            this.log( 'Checkout timed out; starting recovery', this.attempt );
            this.recover();
        }
    }

    onRecoveryTimeout() {
        this.recoveryTimer = null;
        this.log( 'Recovery deadline reached; continuing to checkout' );

        if ( this.recoveryController !== null ) {
            this.recoveryController.abort();
        }

        this.checkout();
    }

    async recover() {
        const attempt = this.attempt;

        if ( attempt !== null && this.destroyed === false ) {
            this.recoveryController = new AbortController();
            this.recoveryTimer = window.setTimeout( this.onRecoveryTimeoutBound, this.config.recoveryTimeoutMs );

            try {
                if ( attempt.wantsProtection === true ) {
                    await this.ensureProtection( this.recoveryController.signal );
                }
            } catch ( error ) {
                this.log( 'Recovery failed; continuing to checkout', error );
            } finally {
                if ( this.attempt !== null && this.attempt.id === attempt.id && this.checkoutStarted === false ) {
                    this.checkout();
                }
            }
        }
    }

    async ensureProtection( signal ) {
        const variantId = Number( this.config.protectionVariantId );
        const base = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
        let protectionExists = false;

        if ( Number.isSafeInteger( variantId ) === false || variantId <= 0 ) {
            this.log( 'No verified protection variant configured; skipping addition' );
        } else {
            const cart = await this.getCart( base, signal );

            for ( let i = 0; i < cart.items.length; i++ ) {
                if ( Number( cart.items[i].variant_id ) === variantId ) {
                    protectionExists = true;
                    break;
                }
            }

            if ( protectionExists === true ) {
                this.log( 'Protection variant already in cart' );
            } else {
                const addResponse = await fetch( base + 'cart/add.js', {
                    method: 'POST',
                    credentials: 'same-origin',
                    signal: signal,
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify( {
                        items: [
                            {
                                id: variantId,
                                quantity: 1,
                                properties: this.config.protectionProperties
                            }
                        ]
                    } )
                } );

                if ( addResponse.ok === false ) {
                    throw new Error( 'Protection add failed: ' + addResponse.status );
                }

                this.log( 'Protection add accepted; verifying cart' );

                const verifiedCart = await this.getCart( base, signal );
                let verified = false;

                for ( let i = 0; i < verifiedCart.items.length; i++ ) {
                    if ( Number( verifiedCart.items[i].variant_id ) === variantId ) {
                        verified = true;
                        break;
                    }
                }

                if ( verified === false ) {
                    throw new Error( 'Protection not found in cart after add' );
                }

                this.log( 'Protection verified in cart' );
            }
        }
    }

    async getCart( base, signal ) {
        const response = await fetch( base + 'cart.js', {
            credentials: 'same-origin',
            cache: 'no-store',
            signal: signal
        } );
        let cart = null;

        if ( response.ok === false ) {
            throw new Error( 'Could not verify cart: ' + response.status );
        }

        cart = await response.json();

        if ( Array.isArray( cart.items ) === false ) {
            throw new Error( 'Unexpected cart response' );
        }

        return cart;
    }

    checkout() {
        if ( this.checkoutStarted === false && this.attempt !== null && this.destroyed === false ) {
            this.checkoutStarted = true;
            this.clearTimer();
            this.attempt = null;
            this.log( 'Redirecting to checkout' );

            try {
                window.location.assign( '/checkout' );
            } catch ( error ) {
                this.log( 'Checkout navigation failed', error );
                this.checkoutStarted = false;
            }

            if ( this.checkoutStarted === true ) {
                this.navigationTimer = window.setTimeout( this.onNavigationTimeoutBound, 5000 );
            }
        }
    }

    onNavigationTimeout() {
        this.navigationTimer = null;

        if ( this.checkoutStarted === true && this.destroyed === false ) {
            this.log( 'Checkout navigation did not unload page; allowing another attempt' );
            this.checkoutStarted = false;
        }
    }

    clearTimer() {
        if ( this.timer !== null ) {
            window.clearTimeout( this.timer );
            this.timer = null;
        }

        if ( this.recoveryTimer !== null ) {
            window.clearTimeout( this.recoveryTimer );
            this.recoveryTimer = null;
        }
    }

    onPageHide() {
        this.clearTimer();

        if ( this.navigationTimer !== null ) {
            window.clearTimeout( this.navigationTimer );
            this.navigationTimer = null;
        }

        if ( this.recoveryController !== null ) {
            this.recoveryController.abort();
        }

        this.attempt = null;
        this.checkoutStarted = true;
    }

    testFailure( wantsProtection = true ) {
        if ( this.config.debug === true && this.attempt === null && this.checkoutStarted === false ) {
            this.startAttempt( wantsProtection );

            if ( this.attempt !== null ) {
                this.log( 'Simulating AfterShip checkout failure', {
                    attemptId: this.attempt.id,
                    wantsProtection: this.attempt.wantsProtection,
                    recovering: this.attempt.recovering,
                    protectionVariantId: this.config.protectionVariantId,
                    protectionProperties: this.config.protectionProperties,
                    timeoutMs: this.config.timeoutMs,
                    recoveryTimeoutMs: this.config.recoveryTimeoutMs,
                    testMode: true
                } );

                this.clearTimer();
                this.onTimeout();
            }
        }
    }

    destroy() {
        this.destroyed = true;
        this.clearTimer();
        this.disconnectButtons();
        this.disconnectWidgetObserver();

        if ( this.observer !== null ) {
            this.observer.disconnect();
            this.observer = null;
        }

        if ( this.initializationTimer !== null ) {
            window.clearInterval( this.initializationTimer );
            this.initializationTimer = null;
        }

        if ( this.navigationTimer !== null ) {
            window.clearTimeout( this.navigationTimer );
            this.navigationTimer = null;
        }

        if ( this.recoveryController !== null ) {
            this.recoveryController.abort();
        }

        this.attempt = null;
        document.removeEventListener( 'DOMContentLoaded', this.onDocumentReadyBound );
        window.removeEventListener( 'pagehide', this.onPageHideBound );
    }
}

window.aftershipWatchdog = new AftershipWatchdog( window.aftershipWatchdogConfig || {} );